import { useCallback, useEffect, useRef, useState, type ChangeEvent } from "react";

import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Spinner } from "@/components/ui/Spinner";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/lib/api/client";
import { scrollToFormError } from "@/lib/scroll-to-form-error";
import { usePdfPasswordPrompt } from "@/hooks/usePdfPasswordPrompt";
import {
  deleteApplicationDocumentFile,
  DOC_KEY_ADDITIONAL_DOCUMENTS,
  listApplicationDocuments,
  listDocumentMappings,
  uploadApplicationDocument,
  type ApplicationDocument,
  type ApplicationDocumentFile,
  type DocumentMapping,
} from "@/services/documents";

type SupportingDocsPanelProps = {
  applicationUuid: string;
  canDelete?: boolean;
  onChanged?: () => Promise<void> | void;
};

type PendingFile = {
  id: string;
  file: File;
  description: string;
};

function fileDescription(file: ApplicationDocumentFile): string {
  const value = file.metadata?.description;
  return typeof value === "string" ? value : value != null ? String(value) : "";
}

function formatUploadedAt(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

export function SupportingDocsPanel({
  applicationUuid,
  canDelete = true,
  onChanged,
}: SupportingDocsPanelProps) {
  const toast = useToast();
  const { uploadFileWithPasswordPrompt, renderPasswordModal } = usePdfPasswordPrompt();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingIdRef = useRef(0);

  const [mapping, setMapping] = useState<DocumentMapping | null>(null);
  const [document, setDocument] = useState<ApplicationDocument | null>(null);
  const [pending, setPending] = useState<PendingFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingFileUuid, setDeletingFileUuid] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    const [mappings, docs] = await Promise.all([
      listDocumentMappings({ document_key: DOC_KEY_ADDITIONAL_DOCUMENTS }),
      listApplicationDocuments(applicationUuid),
    ]);
    setMapping(mappings[0] ?? null);
    setDocument(docs.find((d) => d.document_key === DOC_KEY_ADDITIONAL_DOCUMENTS) ?? null);
  }, [applicationUuid]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        await load();
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof ApiError ? err.message : "Unable to load supporting documents",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  const files = document?.document_files ?? [];

  const onPickFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (!selected.length) return;
    setPending((prev) => [
      ...prev,
      ...selected.map((file) => {
        pendingIdRef.current += 1;
        return { id: String(pendingIdRef.current), file, description: "" };
      }),
    ]);
    setFieldErrors({});
    setError(null);
  };

  const updatePendingDescription = (id: string, description: string) => {
    setPending((prev) =>
      prev.map((item) => (item.id === id ? { ...item, description } : item)),
    );
    setFieldErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const removePending = (id: string) => {
    setPending((prev) => prev.filter((item) => item.id !== id));
    setFieldErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const onUpload = async () => {
    if (!mapping) {
      setError("Supporting documents are not configured.");
      scrollToFormError();
      return;
    }
    if (!pending.length) return;

    const nextErrors: Record<string, string> = {};
    for (const item of pending) {
      if (!item.description.trim()) {
        nextErrors[item.id] = "Description is required.";
      }
    }
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setError("Add a description for each document before uploading.");
      scrollToFormError();
      return;
    }

    setUploading(true);
    setError(null);
    try {
      let documentUuid = document?.uuid ?? null;
      for (const item of pending) {
        await uploadFileWithPasswordPrompt(item.file, (pdfPassword) =>
          uploadApplicationDocument(applicationUuid, {
            documentTypeId: mapping.document_type,
            file: item.file,
            documentUuid,
            metadata: { description: item.description.trim() },
            pdfPassword,
          }),
        );
        if (!documentUuid) {
          const refreshed = await listApplicationDocuments(applicationUuid);
          documentUuid =
            refreshed.find((d) => d.document_key === DOC_KEY_ADDITIONAL_DOCUMENTS)
              ?.uuid ?? null;
        }
      }
      setPending([]);
      await load();
      toast.success(
        pending.length === 1
          ? "Supporting document uploaded."
          : "Supporting documents uploaded.",
      );
      await onChanged?.();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Upload failed. Try again.";
      setError(message);
      toast.error(message);
      scrollToFormError();
    } finally {
      setUploading(false);
    }
  };

  const onDeleteFile = async (fileUuid: string) => {
    if (!canDelete || !document) return;
    setDeletingFileUuid(fileUuid);
    setError(null);
    try {
      await deleteApplicationDocumentFile(applicationUuid, {
        documentUuid: document.uuid,
        documentFileUuid: fileUuid,
      });
      await load();
      toast.success("Supporting document deleted.");
      await onChanged?.();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Unable to delete file.";
      setError(message);
      toast.error(message);
    } finally {
      setDeletingFileUuid(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="size-7" />
      </div>
    );
  }

  return (
    <section className="rounded-2xl border border-line bg-surface-raised p-5 sm:p-6">
      {renderPasswordModal()}
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-ink">Supporting documents</h2>
          <p className="mt-1 text-sm text-muted">
            Upload any additional documents related to this proposal. Each file needs a
            description.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={!mapping}
          onClick={() => fileInputRef.current?.click()}
        >
          + Add documents
        </Button>
      </div>

      {error ? <Alert className="mb-4">{error}</Alert> : null}
      {!mapping ? (
        <Alert className="mb-4" tone="info">
          Supporting documents are not configured for partner upload yet.
        </Alert>
      ) : null}
      {mapping && !canDelete && files.length > 0 ? (
        <Alert className="mb-4" tone="info">
          Existing supporting documents cannot be removed after this loan is verified.
        </Alert>
      ) : null}

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept=".pdf,.jpg,.jpeg,.png,.webp"
        multiple
        onChange={onPickFiles}
      />

      {pending.length > 0 ? (
        <div className="mb-5 rounded-xl border border-line bg-surface p-4">
          <p className="mb-3 text-sm font-medium text-ink">Documents to upload</p>
          <div className="space-y-3">
            {pending.map((item) => (
              <div
                key={item.id}
                className="grid gap-3 rounded-xl border border-line bg-surface-raised p-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_auto] sm:items-start"
              >
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted">
                    File
                  </p>
                  <p className="mt-1 break-all text-sm font-medium text-ink">
                    {item.file.name}
                  </p>
                </div>
                <Input
                  label="Description *"
                  name={`supporting_doc_${item.id}`}
                  value={item.description}
                  error={fieldErrors[item.id]}
                  placeholder="What is this document?"
                  onChange={(e) => updatePendingDescription(item.id, e.target.value)}
                />
                <div className="flex sm:justify-end sm:pt-7">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => removePending(item.id)}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-end">
            <Button
              type="button"
              size="sm"
              loading={uploading}
              disabled={!mapping}
              onClick={() => void onUpload()}
            >
              Upload
            </Button>
          </div>
        </div>
      ) : null}

      {files.length === 0 ? (
        <p className="rounded-xl border border-dashed border-line px-4 py-8 text-center text-sm text-muted">
          No supporting documents uploaded yet.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-line text-xs uppercase tracking-wide text-muted">
                <th className="px-2 py-2 font-medium">Description</th>
                <th className="px-2 py-2 font-medium">File</th>
                <th className="px-2 py-2 font-medium">Uploaded</th>
                {canDelete ? <th className="px-2 py-2 font-medium">Action</th> : null}
              </tr>
            </thead>
            <tbody>
              {files.map((file) => (
                <tr key={file.uuid} className="border-b border-line align-top">
                  <td className="px-2 py-3 text-ink">{fileDescription(file) || "—"}</td>
                  <td className="px-2 py-3">
                    {file.file_url ? (
                      <a
                        href={file.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="break-all font-medium text-brand hover:underline"
                      >
                        {file.file_name}
                      </a>
                    ) : (
                      <span className="break-all text-ink">{file.file_name}</span>
                    )}
                  </td>
                  <td className="px-2 py-3 text-muted">{formatUploadedAt(file.uploaded_at)}</td>
                  {canDelete ? (
                    <td className="px-2 py-3">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        loading={deletingFileUuid === file.uuid}
                        onClick={() => void onDeleteFile(file.uuid)}
                      >
                        Delete
                      </Button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
